/**
 * Lista richieste di preventivo (RDP) con filtri stato e anno.
 * Bottone "Nuova richiesta" naviga a NuovaRichiesta.
 * Click riga naviga a RichiestaDetail.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState, useUrlNumberState } from '@lib/useUrlState';
import { Button, Card } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { StatoRichiestaBadge } from '../components/StatoRichiestaBadge';

interface Richiesta {
  id: string;
  numero: string;
  anno: number;
  stato: string;
  data_richiesta: string;
  data_scadenza_risposta: string | null;
  valuta: string;
  note: string | null;
}

const STATI_LABEL: Record<string, string> = {
  bozza: 'Bozza',
  inviata: 'Inviata',
  parzialmente_risposta: 'Parziale',
  completata: 'Completata',
  annullata: 'Annullata',
};

export function RichiesteList() {
  const navigate = useNavigate();
  const { date } = useFormatters();
  const [richieste, setRichieste] = useState<Richiesta[]>([]);
  const [loading, setLoading] = useState(true);
  const [anno, setAnno] = useUrlNumberState('anno', new Date().getFullYear());
  const [statoFiltro, setStatoFiltro] = useUrlState('stato', '');

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchRichieste = async () => {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('anno', String(anno));
      if (statoFiltro) params.set('stato', statoFiltro);

      const res = await fetch(`/api/v1/richieste?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setRichieste(await res.json());
      }
      setLoading(false);
    };
    fetchRichieste();
  }, [anno, statoFiltro, token]);

  const stati = ['', 'bozza', 'inviata', 'parzialmente_risposta', 'completata', 'annullata'];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-outfit font-semibold text-foreground">
          Richieste di preventivo
        </h1>
        <Button onClick={() => navigate('/richieste/nuova')}>Nuova richiesta</Button>
      </div>

      <div className="flex gap-3">
        <select
          value={anno}
          onChange={(e) => setAnno(Number(e.target.value))}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          {[2024, 2025, 2026, 2027].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={statoFiltro}
          onChange={(e) => setStatoFiltro(e.target.value)}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          {stati.map((s) => (
            <option key={s || '__all__'} value={s}>
              {s ? (STATI_LABEL[s] || s) : 'Tutti gli stati'}
            </option>
          ))}
        </select>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento...</div>
        ) : richieste.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Nessuna richiesta trovata
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Numero</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Data</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Stato</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Scadenza</th>
              </tr>
            </thead>
            <tbody>
              {richieste.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/richieste/${r.id}`)}
                  className="border-b border-border hover:bg-muted/30 cursor-pointer transition-colors duration-150"
                >
                  <td className="px-4 py-2 font-medium">{r.numero}</td>
                  <td className="px-4 py-2">{date(r.data_richiesta)}</td>
                  <td className="px-4 py-2">
                    <StatoRichiestaBadge stato={r.stato} />
                  </td>
                  <td className="px-4 py-2">
                    {r.data_scadenza_risposta ? date(r.data_scadenza_risposta) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
